/**
 * Notifications Step
 *
 * Request browser notification permission.
 * Issue #563 - First-Run Onboarding Flow
 */

import { useState } from 'react';
import { ChevronLeft, ArrowRight } from 'lucide-react';
import PhotoAttribution from '@/components/PhotoAttribution';
import ImageWithSkeleton from '@/components/ImageWithSkeleton';

interface NotificationsStepProps {
  onNext: () => void;
  onBack: () => void;
}

export default function NotificationsStep({ onNext, onBack }: NotificationsStepProps) {
  const [status, setStatus] = useState<'idle' | 'requesting' | 'granted' | 'denied'>('idle');

  const handleEnableNotifications = async () => {
    setStatus('requesting');

    try {
      if (!('Notification' in window)) {
        // Browser doesn't support notifications
        setStatus('idle');
        onNext();
        return;
      }

      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        setStatus('granted');
        setTimeout(() => onNext(), 500);
      } else {
        setStatus('denied');
      }
    } catch (error) {
      console.error('Notification permission error:', error);
      setStatus('idle');
      onNext(); // Continue anyway
    }
  };

  const isRequesting = status === 'requesting';
  const isGranted = status === 'granted';
  const isDenied = status === 'denied';

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Progress Bar - Step 6 of 7 */}
      <div className="shrink-0 px-6 pt-4">
        <div className="w-full h-1 bg-oro-surface-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-oro-gold rounded-full transition-all duration-300"
            style={{ width: '85%' }}
          />
        </div>
        <p className="text-text-muted text-xs mt-2 text-center">Step 6 of 7</p>
      </div>

      {/* Hero Image - Full Height */}
      <div className="flex-1 relative m-4 rounded-xl overflow-hidden">
        <ImageWithSkeleton
          src="/images/onboarding/hero-vertical-mtb-forest-group-jakubcihak_cp.jpg"
          alt="Mountain bikers riding together through a forest"
          loading="eager"
          className="w-full h-full object-cover"
        />
        <PhotoAttribution photographer="Jakub Čihák" unsplashUsername="jakubcihak" />

        {/* Title overlay on image */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
          <h2 id="onboarding-title" className="text-2xl font-semibold text-white mb-1">
            Stay in the loop
          </h2>
          {status === 'granted' && (
            <p className="text-oro-success text-sm font-medium">Notifications enabled!</p>
          )}
          {status === 'denied' && (
            <p className="text-white/70 text-sm">You can enable later in settings.</p>
          )}
        </div>
      </div>

      {/* Bottom-Docked Actions */}
      <div className="shrink-0 p-4 border-t border-oro-border bg-oro-surface">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isRequesting}
            className="px-4 py-3 rounded-lg bg-oro-surface-alt border border-oro-border text-text-primary hover:bg-oro-surface-alt/80 transition-colors disabled:opacity-50"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Main action button */}
          {!isGranted && !isDenied ? (
            <>
              <button
                type="button"
                onClick={handleEnableNotifications}
                disabled={isRequesting}
                className="flex-1 px-4 py-3 rounded-lg bg-oro-gold hover:bg-oro-gold/90 shadow-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
              >
                <span className="text-black font-medium">
                  {isRequesting ? 'Enabling...' : 'Enable Notifications'}
                </span>
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={isRequesting}
                className="px-4 py-3 rounded-lg text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
              >
                Skip
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onNext}
              className="flex-1 px-4 py-3 rounded-lg bg-oro-gold hover:bg-oro-gold/90 shadow-lg flex items-center justify-center gap-2 transition-all"
            >
              <span className="text-black font-medium">Continue</span>
              <ArrowRight className="w-5 h-5 text-black" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
